import { theme } from 'src/theme';
import type { SxProp } from 'src/types/mui';

import { TableThemeStyle, TableThemes } from '../types';

import { ACTIONS_COLUMN_ID, SELECTION_COLUMN_ID } from './constants';

export const TableStyles = {
  [TableThemes.default]: {
    border: 0,
    padding: 0,
    tableTopMenuPadding: '16px',
    tableCellPadding: '8px 16px',
    tableHeadCellPadding: '0 16px',
    tableFooterBorder: 'transparent',
  },
  [TableThemes.withBorder]: {
    border: `1px solid ${theme.palette.border?.main}`,
    padding: 0,
    tableTopMenuPadding: '0 0 16px',
    tableCellPadding: '8px 16px',
    tableHeadCellPadding: '0 16px',
    tableFooterBorder: 'transparent',
  },
  [TableThemes.withBorderWithoutProfileIcon]: {
    border: `1px solid ${theme.palette.border?.main}`,
    padding: 0,
    tableTopMenuPadding: '16px 0 16px',
    tableCellPadding: '16px',
    tableHeadCellPadding: '0 16px',
    tableFooterBorder: 'transparent',
  },
  [TableThemes.withBorderWhole]: {
    border: `1px solid ${theme.palette.border?.main}`,
    padding: 0,
    tableTopMenuPadding: '0 0 16px',
    tableCellPadding: '16px',
    tableHeadCellPadding: '0 16px',
    tableFooterBorder: 'transparent',
  },
  [TableThemes.withoutProfileIcon]: {
    border: 0,
    padding: 0,
    tableTopMenuPadding: '16px',
    tableCellPadding: '16px',
    tableHeadCellPadding: '0 16px',
    tableFooterBorder: 'transparent',
  },
  [TableThemes.hiddenResult]: {
    border: 0,
    padding: 0,
    tableTopMenuPadding: '0 16px 16px',
    tableCellPadding: '8px 16px',
    tableHeadCellPadding: '0 16px',
    tableFooterBorder: 'transparent',
  },
} as const satisfies Record<TableThemes, TableThemeStyle>;

export type TableColumnKind = 'data' | 'selection' | 'actions';

export const getColumnKind = (columnId: string): TableColumnKind => {
  if (columnId === SELECTION_COLUMN_ID) return 'selection';
  if (columnId === ACTIONS_COLUMN_ID) return 'actions';
  return 'data';
};

export const columnKindStyles: Record<TableColumnKind, SxProp | undefined> = {
  data: undefined,
  selection: {
    width: 48,
    minWidth: 'unset',
    padding: '0 0 0 4px',
  },
  // sticky right replaces DataGrid column pinning; only visible when the table overflows horizontally
  actions: theme => ({
    position: 'sticky',
    right: 0,
    width: 100,
    minWidth: 100,
    // centers the 'Actions' header text and the row-actions button, matching the old grid
    textAlign: 'center',
    // longhand on purpose: the 'background' shorthand would reset backgroundColor set by
    // earlier layers; body cells inherit the row background (white/error/hover)
    backgroundColor: 'inherit',
    // separator standing in for the old right-pinned column divider (header + body cells).
    // box-shadow, not border-left: MUI's table is border-collapse: collapse, where a border on
    // a position: sticky cell is painted on the shared grid and disappears on horizontal
    // scroll; an inset shadow rides the cell's own layer and stays put.
    boxShadow: `inset 1px 0 0 0 ${theme.palette.divider}`,
  }),
};
